import {
  INVULNERABILITY_MS,
  RUN_DURATION_MS,
  STARTING_LIVES,
} from "../constants";
import { applyHazardHit, createLifeState, isInvulnerable, type LifeState } from "./lives";
import { addHazardClearScore, addScore } from "./scoring";

export type RunPhase = "playing" | "finishing" | "completed" | "gameOver" | "paused";

export interface RunSnapshot {
  elapsedMs: number;
  remainingMs: number;
  score: number;
  lives: number;
  phase: RunPhase;
  completedCave: boolean;
  progress: number;
}

export class RunState {
  elapsedMs = 0;
  score = 0;
  phase: RunPhase = "playing";
  completedCave = false;
  private livesState: LifeState;
  private readonly durationMs: number;
  private readonly invulnMs: number;

  constructor(
    lives = STARTING_LIVES,
    durationMs = RUN_DURATION_MS,
    invulnMs = INVULNERABILITY_MS,
  ) {
    this.livesState = createLifeState(lives);
    this.durationMs = durationMs;
    this.invulnMs = invulnMs;
  }

  get lives(): number {
    return this.livesState.lives;
  }

  get remainingMs(): number {
    return Math.max(0, this.durationMs - this.elapsedMs);
  }

  get progress(): number {
    return Math.min(1, this.elapsedMs / this.durationMs);
  }

  get isActive(): boolean {
    return this.phase === "playing";
  }

  tick(deltaMs: number): { justFinished: boolean } {
    if (this.phase !== "playing") {
      return { justFinished: false };
    }
    this.elapsedMs = Math.min(this.durationMs, this.elapsedMs + deltaMs);
    if (this.elapsedMs >= this.durationMs) {
      this.phase = "finishing";
      this.completedCave = true;
      return { justFinished: true };
    }
    return { justFinished: false };
  }

  collectFood(foodId: string, crouched = false): number {
    if (this.phase !== "playing" && this.phase !== "finishing") {
      return 0;
    }
    const before = this.score;
    this.score = addScore(this.score, foodId, crouched);
    return this.score - before;
  }

  clearHazard(): number {
    if (this.phase !== "playing" && this.phase !== "finishing") {
      return 0;
    }
    const before = this.score;
    this.score = addHazardClearScore(this.score);
    return this.score - before;
  }

  hitHazard(): { applied: boolean; gameOver: boolean } {
    if (this.phase !== "playing") {
      return { applied: false, gameOver: this.phase === "gameOver" };
    }
    const result = applyHazardHit(this.livesState, this.elapsedMs, this.invulnMs);
    this.livesState = {
      lives: result.lives,
      invulnerableUntilMs: result.invulnerableUntilMs,
    };
    if (result.gameOver) {
      this.phase = "gameOver";
      this.completedCave = false;
    }
    return { applied: result.applied, gameOver: result.gameOver };
  }

  isInvulnerable(): boolean {
    return isInvulnerable(this.livesState, this.elapsedMs);
  }

  pause(): void {
    if (this.phase === "playing") {
      this.phase = "paused";
    }
  }

  resume(): void {
    if (this.phase === "paused") {
      this.phase = "playing";
    }
  }

  markCompleted(): void {
    if (this.completedCave) {
      this.phase = "completed";
    }
  }

  snapshot(): RunSnapshot {
    return {
      elapsedMs: this.elapsedMs,
      remainingMs: this.remainingMs,
      score: this.score,
      lives: this.lives,
      phase: this.phase,
      completedCave: this.completedCave,
      progress: this.progress,
    };
  }
}

export function canSubmitHighScore(snapshot: Pick<RunSnapshot, "completedCave" | "phase">): boolean {
  return snapshot.completedCave && snapshot.phase !== "gameOver";
}
